import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getProductManufacturers,
  getProductModels,
  getProductTypes,
  resetFilters,
  updateFilter,
} from "../../../store/slices/ProductFinder/ProductFinderSlice";
import CheckBox from "../../atoms/CheckBox/CheckBox";
import AutoComplete from "../../atoms/AutoComplete";

// Main product filtering component
export default function ProductFilter() {
  const dispatch = useDispatch();
  const {
    productTypes,
    filterObject,
    manufacturers,
    models,
    productCategories: selectedCategories,
    allCategories,
  } = useSelector((state) => state.productFinder);

  // UI state
  const [open, setOpen] = useState(true); // toggle open/close for filters
  const [manufacturersSet, setManufacturersSet] = useState([]); // local cache for selected manufacturers
  const [modelSet, setModelSet] = useState([]); // local cache for selected models
  const [categoriesSet, setCategoriesSet] = useState([]); // local cache for selected categories

  // Initialize local state from Redux store on component mount
  useEffect(() => {
    setManufacturersSet(filterObject.manufacturers || []);
    setModelSet(filterObject.models || []);
    setCategoriesSet(selectedCategories || []);
  }, [filterObject.manufacturers, filterObject.models, selectedCategories]);

  // Fetch product types once on initial render
  useEffect(() => {
    dispatch(getProductTypes());
  }, [dispatch]);

  // Fetch manufacturers whenever selected product types change
  useEffect(() => {
    if (filterObject.types.length > 0) dispatch(getProductManufacturers());
  }, [dispatch, filterObject.types]);

  // Fetch models whenever selected manufacturers change
  useEffect(() => {
    dispatch(getProductModels());
  }, [dispatch, manufacturersSet]);

  // Toggle logic for product type checkboxes
  const toggleFilter = (key) => {
    if (!filterObject.types.includes(key)) {
      dispatch(
        updateFilter({ ...filterObject, types: [...filterObject.types, key] })
      );
    } else {
      dispatch(
        updateFilter({
          ...filterObject,
          types: filterObject.types.filter((obj) => obj !== key),
        })
      );
    }
  };

  // Handlers for adding/removing manufacturers
  const handleAddManufacturer = (val) => {
    if (!val?.label || filterObject.manufacturers.includes(val.label)) return;
    const updated = [...filterObject.manufacturers, val.label];
    dispatch(updateFilter({ ...filterObject, manufacturers: updated }));
    setManufacturersSet(updated);
  };

  const handleRemoveManufacturer = (label) => {
    const updated = filterObject.manufacturers.filter((m) => m !== label);
    dispatch(updateFilter({ ...filterObject, manufacturers: updated }));
    setManufacturersSet(updated);
  };

  // Handlers for adding/removing models
  const handleAddModel = (val) => {
    if (!val?.label || filterObject.models.includes(val.label)) return;
    const updated = [...filterObject.models, val.label];
    dispatch(updateFilter({ ...filterObject, models: updated }));
    setModelSet(updated);
  };

  const handleRemoveModel = (label) => {
    const updated = filterObject.models.filter((m) => m !== label);
    dispatch(updateFilter({ ...filterObject, models: updated }));
    setModelSet(updated);
  };

  // Handlers for adding/removing categories
  const handleAddCategory = (val) => {
    const category = val?.label || val;
    if (!category || selectedCategories?.includes(category)) return;
    const updated = [...selectedCategories, category];
    dispatch(updateFilter({ ...filterObject, productCategories: updated }));
  };

  const handleRemoveCategory = (label) => {
    const updated = selectedCategories?.filter((c) => c !== label);
    dispatch(updateFilter({ ...filterObject, productCategories: updated }));
  };

  // Reset all filters
  const handleResetFilters = () => {
    dispatch(resetFilters());
    setManufacturersSet([]);
    setModelSet([]);
  };

  const labelFunction = useCallback((option) => option?.label || "", []);

  return (
    <div className="bg-white/40 shadow-sm rounded-md border border-gray-200 mb-4 ml-2 mt-5">
      {/* Header with toggle and reset */}
      <div className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-800 bg-gray-100/70 hover:bg-gray-200">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 focus:outline-none"
        >
          <span>Product Filters</span>
          <svg
            className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <button
          onClick={handleResetFilters}
          className="text-sm text-[var(--primary-color)] hover:text-[var(--secondary-color)] focus:outline-none"
          title="Reset all filters"
        >
          Reset All
        </button>
      </div>

      {/* Category selection */}
      <h2 className="mt-1 ml-2 font-semibold">Categories</h2>
      <AutoComplete
        options={allCategories
          .filter((category) => !selectedCategories?.includes(category))
          .map((category, idx) => ({ id: idx, label: category }))}
        onSelect={handleAddCategory}
        labelFunction={labelFunction}
        className="mx-2"
        clearOnSelect
        maxItems="none"
      />
      <div className="flex flex-wrap gap-2 mt-2 px-4 pb-4">
        {selectedCategories?.map((label, idx) => (
          <Tag
            key={idx}
            label={label}
            onRemove={() => handleRemoveCategory(label)}
          />
        ))}
      </div>

      {/* Product type filters */}
      <h2 className="ml-2 font-semibold">Product Type</h2>
      {open && (
        <div className="p-4 flex flex-wrap gap-x-4 gap-y-2">
          {productTypes.map((label) => (
            <CheckBox
              key={label}
              checked={filterObject.types.includes(label)}
              onChange={() => toggleFilter(label)}
              label={label}
            />
          ))}
        </div>
      )}

      {/* Manufacturer selection */}
      <h2 className="ml-2 font-semibold">Manufacturers</h2>
      <AutoComplete
        options={manufacturers.filter(
          (item) => !filterObject.manufacturers.includes(item.label)
        )}
        onSelect={handleAddManufacturer}
        labelFunction={labelFunction}
        className="mx-2"
        disabled={filterObject.types.length === 0}
        clearOnSelect
        disabledMessage="You must select at least one product type"
        maxItems="none"
      />
      <div className="flex flex-wrap gap-2 mt-2 px-4 pb-4">
        {filterObject.manufacturers.map((label, idx) => (
          <Tag
            key={idx}
            label={label}
            onRemove={() => handleRemoveManufacturer(label)}
          />
        ))}
      </div>

      {/* Model selection */}
      <h2 className="ml-2 font-semibold">Models</h2>
      <AutoComplete
        options={models.filter(
          (item) => !filterObject.models.includes(item.label)
        )}
        onSelect={handleAddModel}
        labelFunction={labelFunction}
        className="mx-2"
        disabled={
          filterObject.manufacturers.length === 0 ||
          filterObject.types.length === 0
        }
        clearOnSelect
        disabledMessage="You must select at least one manufacturer"
        maxItems="none"
      />
      <div className="flex flex-wrap gap-2 mt-2 px-4 pb-4">
        {modelSet.map((label, idx) => (
          <Tag
            key={idx}
            label={label}
            onRemove={() => handleRemoveModel(label)}
          />
        ))}
      </div>
    </div>
  );
}

// Reusable Tag display for selected manufacturers/models
function Tag({ label, onRemove }) {
  return (
    <div className="flex items-center bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm">
      <span>{label}</span>
      <button
        onClick={onRemove}
        className="ml-2 text-gray-600 hover:text-red-500 focus:outline-none"
        title="Remove"
      >
        ×
      </button>
    </div>
  );
}
